/**
 * Error Handler Hook
 * Provides error handling functionality with toast integration
 */

import { useEffect } from 'react';
import { errorHandler, handleError, handleApiError, handleRoutingError } from '../services/errorHandler';
import { useCyclingToasts } from './useCyclingToasts';
import type { ErrorContext } from '../types/errors';

export const useErrorHandler = () => {
  const toasts = useCyclingToasts();

  // Initialize error handler with toast service
  useEffect(() => {
    errorHandler.setToastService(toasts);
  }, [toasts]);

  return {
    // Core error handling functions
    handleError: (error: Error | string, context?: ErrorContext) => 
      handleError(error, context),
    
    handleApiError: (error: any, endpoint: string, operation: string, params?: Record<string, any>) =>
      handleApiError(error, endpoint, operation, params),
    
    handleRoutingError: (error: any, operation: string, routeData?: any) =>
      handleRoutingError(error, operation, routeData),

    // Convenience methods for common scenarios
    handleNetworkError: (error: any, operation: string = 'network request') => 
      handleError(error, { 
        feature: 'network', 
        operation 
      }),

    handleAuthError: (error: any, operation: string = 'authentication') =>
      handleError(error, { 
        feature: 'auth', 
        operation 
      }),

    handleValidationError: (error: any, field?: string, operation: string = 'validation') =>
      handleError(error, { 
        feature: 'validation', 
        operation,
        parameters: { field }
      }),

    handleStorageError: (error: any, operation: string = 'storage operation') =>
      handleError(error, { 
        feature: 'storage', 
        operation 
      }),

    handleGeocodingError: (error: any, query?: string, operation: string = 'geocoding') =>
      handleError(error, { 
        feature: 'geocoding', 
        operation,
        parameters: { query }
      }),

    // Error handler configuration
    setUserId: (userId: string | undefined) => {
      errorHandler.setUserId(userId);
    },

    // Direct access to error handler for advanced usage
    errorHandler
  };
};

export default useErrorHandler;
